import {
  deletePackage,
  exportPackages,
  getPackageExportStatus,
  getPackageList,
} from 'app/api/package';
import { VOUCHER_PASSWORD } from 'app/constants/api-contants';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useNavigate } from 'react-router-dom';

const DEFAULT_FILTERS = { status: '' };
const DATA_PER_PAGE = 10;

// `status` is sent only when one is selected. fetchData in api/comonsearch.js
// drops undefined/null/'' params, and the server validates `status` against
// its enum, so "all" is expressed by leaving the key out, never by a sentinel.
// The header's mall selection is deliberately NOT forwarded: the server scopes
// the list to the caller's own malls (D-16), and the header value is a
// comma-joined string.
const withStatus = (params, filters) => {
  if (filters?.status) {
    return { ...params, status: filters.status };
  }
  return params;
};

const usePackageManagement = () => {
  const navigate = useNavigate();

  const [isExporting, setIsExporting] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [isdeleteFiltering, setIsdeleteFiltering] = useState(false);
  const dataPerPage = DATA_PER_PAGE;

  // Draft filter the panel edits vs the applied filter the table consumes as
  // `values`. They must stay separate objects or PaginationTable refetches on
  // every keystroke.
  const [filterValues, setFilterValues] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  // Password gate (REQ-01): its own copy, mirroring useVoucherManagement.
  const [passwordmodelOpen, setPasswordmodelOpen] = useState(false);
  const [passwordVal, setPasswordVal] = useState('');
  const [key, setkey] = useState('');
  const [tempdata, settempdata] = useState({});

  // The table owns the search box and hands the text to fetchData; remember the
  // last value so the export matches what the list is showing (D-12).
  const searchRef = useRef('');

  // Stable identity: PaginationTable fires its fetch effect on `fetchData`
  // identity, so this may depend only on dataPerPage.
  const fetchPackageData = useCallback(
    async (page, search = '', filters = {}) => {
      searchRef.current = search;
      setPageLoading(true);
      try {
        const params = withStatus({ page, limit: dataPerPage, search }, filters);
        const response = await getPackageList(params);
        if (response?.responseCode === 200) {
          setFilteredData(response?.data?.data || []);
          const totalCount = response?.data?.count || 0;
          setTotalNumber(totalCount);
          setPageCount(Math.ceil(totalCount / dataPerPage));
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setIsdeleteFiltering(false);
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );

  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [isFiltering]);

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setIsFilterOpen(false);
    setIsFiltering(true);
  }

  function handleFilterCancel() {
    setIsFilterOpen(false);
  }

  function resetFilter() {
    setFilterValues(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setIsFiltering(true);
  }

  async function callDeleteAPI(data) {
    setPageLoading(true);
    try {
      const response = await deletePackage(data?.id);
      if (response?.responseCode === 200) {
        setIsdeleteFiltering(true);
        Toast.success(Message.Package.DELETED_SUCCESS);
      } else {
        // The server's "already bought" refusal arrives here and is the real
        // protection; the greyed delete icon is only cosmetic (D-07).
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }

  function handleDelete(data) {
    confirmAlert({
      title: '',
      message: `Are you sure you want to delete the package ${
        data?.title || ''
      }?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            callDeleteAPI(data);
          },
        },
        {
          label: 'No',
        },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  }

  function handleEdit(data) {
    navigate(APP_CONSTANTS.EDIT_PACKAGE.replace(':id', data?.id));
  }

  function handlePasswordModel(data, gateKey) {
    setPasswordmodelOpen(true);
    settempdata(data);
    setkey(gateKey);
  }

  function handlemodelvalues() {
    setPasswordmodelOpen(false);
    setPasswordVal('');

    if (passwordVal !== VOUCHER_PASSWORD.VOUCHER_PASSWORD) {
      Toast.error('Your Entered Password is wrong.');
      return;
    }

    if (key === 'edit') {
      handleEdit(tempdata);
      return;
    }
    if (key === 'delete') {
      handleDelete(tempdata);
      return;
    }
    if (key === 'add') {
      navigate(APP_CONSTANTS.CREATE_PACKAGE);
    }
  }

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportPackages(
        withStatus({ search: searchRef.current }, appliedFilters)
      );
      if (response?.responseCode !== 200) {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
        setIsExporting(false);
        return;
      }

      const jobId = response?.data?.jobId;
      if (!jobId) {
        Toast.error(Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      Toast.success(Message.Package.EXPORT_STARTED);

      startExportPoll(jobId, {
        // Poll the package status route, not the shared one: the shared route
        // resolves to the reporting permission and its 401 logs a voucher-only
        // admin out mid-export.
        getStatus: getPackageExportStatus,
        onDone: (url) => {
          setIsExporting(false);
          triggerDownload(url);
          Toast.success(Message.Package.EXPORT_READY);
        },
        onFailed: (error) => {
          setIsExporting(false);
          Toast.error(error || Message.Error.COMMON_MESSAGE);
        },
        onError: () => {
          setIsExporting(false);
          Toast.error(Message.Error.COMMON_MESSAGE);
        },
      });
    } catch {
      Toast.error(Message.Error.COMMON_MESSAGE);
      setIsExporting(false);
    }
  };

  // The page heading is rendered by the Voucher Management shell above the tabs,
  // so this pane passes no title.
  const sectionObj = {
    searchObj: {
      keys: ['title', 'mallName'],
      placeholder: 'Search by bundle or mall',
      searchType: 'server',
      handleSearch: () => {},
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen((prev) => !prev),
      },
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: isExporting ? 'Exporting...' : 'Export',
        handleBtnClick: handleExport,
        isDisabled: isExporting,
      },
      {
        customclassName: '',
        imgTrue: true,
        title: APP_CONSTANTS.CREATE_PACKAGE_TAG,
        handleBtnClick: (e) => {
          handlePasswordModel(e, 'add');
        },
      },
    ],
  };

  return {
    sectionObj,
    filteredData,
    totalNumber,
    pageCount,
    pageLoading,
    dataPerPage,
    fetchPackageData,
    isdeleteFiltering,
    filterValues,
    setFilterValues,
    appliedFilters,
    isFilterOpen,
    setIsFilterOpen,
    isFiltering,
    handleFilterSubmit,
    handleFilterCancel,
    resetFilter,
    handleDelete,
    handleEdit,
    handleExport,
    isExporting,
    passwordmodelOpen,
    setPasswordmodelOpen,
    passwordVal,
    setPasswordVal,
    handlePasswordModel,
    handlemodelvalues,
  };
};

export default usePackageManagement;
